import { useEffect, useRef, useState } from "react";

interface UseStreamingTypewriterOptions {
  /**
   * The content to type out
   */
  content: string;
  /**
   * Speed of typing in milliseconds per character (default: 3ms for smooth streaming)
   */
  speed?: number;
  /**
   * Whether the typing animation is active
   */
  isActive: boolean;
  /**
   * Callback fired when typing animation completes
   */
  onComplete?: () => void;
}

/**
 * Custom hook that creates a smooth typewriter effect for streaming content.
 * It continuously types out characters one by one, catching up to new chunks as they arrive.
 */
export function useStreamingTypewriter({
  content,
  speed = 3,
  isActive,
  onComplete,
}: UseStreamingTypewriterOptions): string {
  const [displayedContent, setDisplayedContent] = useState("");
  const targetContentRef = useRef("");
  const displayedContentRef = useRef("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const speedRef = useRef(speed);
  const isActiveRef = useRef(isActive);
  const onCompleteRef = useRef(onComplete);
  const hasCalledCompleteRef = useRef(false);

  // Update refs when props change
  useEffect(() => {
    speedRef.current = speed;
    isActiveRef.current = isActive;
    onCompleteRef.current = onComplete;
  }, [speed, isActive, onComplete]);

  // Continuous typing loop that always catches up
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const typeNextCharacter = () => {
      const current = displayedContentRef.current;
      const target = targetContentRef.current;

      // Always catch up if there's a gap
      if (current.length < target.length) {
        // Type next character(s) - type multiple if we're far behind for smoother catch-up
        const charsBehind = target.length - current.length;
        // Type faster (more chars) if we're far behind
        const charsToType = charsBehind > 20 ? 3 : charsBehind > 10 ? 2 : 1;
        
        const nextChars = target.slice(current.length, current.length + charsToType);
        displayedContentRef.current = current + nextChars;
        setDisplayedContent(displayedContentRef.current);

        // Schedule next character
        timeoutRef.current = setTimeout(typeNextCharacter, speedRef.current);
      } else {
        // Caught up - check if we should call onComplete
        if (!isActiveRef.current && target.length > 0 && !hasCalledCompleteRef.current) {
          hasCalledCompleteRef.current = true;
          if (onCompleteRef.current) {
            onCompleteRef.current();
          }
        }
        // Keep checking in case new content arrives
        timeoutRef.current = setTimeout(typeNextCharacter, speedRef.current);
      }
    };

    // Start the typing loop
    typeNextCharacter();

    // Cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []); // Run once on mount

  // Update target content when content changes
  useEffect(() => {
    targetContentRef.current = content;
    // Reset completion flag when new content starts streaming
    if (isActive && content.length > 0) {
      hasCalledCompleteRef.current = false;
    }
  }, [content, isActive]);

  // Reset when content is cleared
  useEffect(() => {
    if (!isActive && content.length === 0) {
      displayedContentRef.current = "";
      setDisplayedContent("");
      hasCalledCompleteRef.current = false;
    }
  }, [isActive, content]);

  return displayedContent;
}
